"use client";
import { Badge, Button, Avatar } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Image from "next/image";
import React, { useState } from "react";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useRouter, usePathname } from "next/navigation";
import {
  calculateTotalAmountWithGST,
  formatWithCommasWithDecimal,
  getTotalCartQuantity,
} from "@/utils/helpers";
import ccLogo from "../../assets/icons/cc-blue.png";
import profileIcon from "../../assets/icons/profile-icon.svg";
import HeaderWhatsApp from "@/middleware/HeaderWhatsapp";
import ViewMyDiscount from "./ViewMyDiscount";
import PopOver from "./PopOver";
import BuildYourCart from "./BuildYourCart";
import Link from "next/link";
import { calculateManufacturerDiscount } from "@/utils/payment";
import { trackEvent } from "@/lib/gtag";
const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

const Header = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { cart } = useAppSelector((state) => state.cart);
  const { userData } = useAppSelector((state) => state.auth);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleViewDiscountClick = () => {
    trackEvent({
      action: "view_my_discount_click",
      category: "engagement",
      label: "View My Discount Button Click",
    });
  };

  return (
    <div className="relative w-full">
      {/* Header */}
      <div className="w-full flex items-center justify-between bg-[#f4f4f4] py-3 px-3 md:px-8">
        {/* Logo */}
        <Image
          quality={50}
          unoptimized
          src={ccLogo}
          alt="Cycle Circle Logo"
          width={70}
          height={50}
          priority
          className="cursor-pointer"
          onClick={() => router.push("/")}
        />

        <div className="flex items-center gap-4 md:gap-6">
          {/* Hide WhatsApp on mobile */}
          <div className="hidden md:block">
            <HeaderWhatsApp />
          </div>

          {/* View My Discount Button */}
          <Button
            variant="contained"
            sx={{
              background:
                "linear-gradient(to bottom, var(--color-blue) 0%, #3A4D6A 100%)",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: "50px",
              fontWeight: "600",
              fontSize: "14px",
              boxShadow: "none",
              textTransform: "capitalize",
              letterSpacing: "0.5px",
              "&:hover": {
                boxShadow: "none",
              },
            }}
            onClick={() => {
              handleOpen();
              handleViewDiscountClick();
            }}
          >
            View My Discount
          </Button>
          <ViewMyDiscount open={open} handleClose={handleClose} />

          {/* Cart Icon with Badge */}
          <Badge
            badgeContent={getTotalCartQuantity(cart)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "var(--color-green)",
                color: "#fff",
                fontSize: "12px",
              },
            }}
          >
            <Link href="/checkout/cart" passHref>
              <ShoppingCartOutlinedIcon
                className="text-black-1"
                style={{ fontSize: "28px", cursor: "pointer" }}
              />
            </Link>
          </Badge>

          {/* Profile Avatar */}
          {userData?.UserImage ? (
            <Avatar
              src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_USER_IMAGE/${
                userData?.UserImage || ""
              }`}
              alt="User Profile"
              sx={{
                width: 38,
                height: 38,
                cursor: "pointer",
                border: "1px solid gray",
              }}
              onClick={handleProfileClick}
            />
          ) : (
            <Image
              quality={50}
              property="no"
              unoptimized
              src={profileIcon}
              alt="Profile"
              width={38}
              height={38}
              style={{ borderRadius: "50%", cursor: "pointer" }}
              onClick={handleProfileClick}
            />
          )}

          {/* Popover Menu */}
          <PopOver anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
        </div>
      </div>

      {/* Bottom Fixed Bar - Only for Home Page */}
      {pathname === "/" && (
        <div
          className="fixed bottom-0 h-[100px] w-full bg-white-1 flex justify-between items-center px-3 md:px-8 py-2 md:py-3 z-10"
          style={{ boxShadow: "0px -1px 6px rgba(0, 0, 0, 0.25)" }}
        >
          {/* Net Total */}
          <div className="text-base md:text-lg font-bold text-blue">
            Net Total: ₹
            {formatWithCommasWithDecimal(
              calculateTotalAmountWithGST(cart) -
                calculateManufacturerDiscount(cart)
            )}
          </div>
          <div className="absolute top-1/3 left-[18%] w-[65%]">
            <BuildYourCart />
          </div>
          {/* View My Cart Button */}
          <Link href="/checkout/cart" passHref>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "var(--color-green)",
                color: "white",
                fontSize: "14px",
                padding: "8px 16px",
                textTransform: "none",
                height: "38px",
                fontWeight: "700",
                boxShadow: "none",
                letterSpacing: "1px",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "var(--color-green-hover)",
                },
              }}
            >
              View My Cart
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Header;
